import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  addToFavorites,
  searchVets,
  validateZipCode,
  vetZipCodeChanged,
  cleanMyVetsErrors,
  searchPrimaryVet,
  savePrimaryVet,
} from '../../../actions/myVets';
import close from '../../../assets/close-button.svg';
import { validateNumbersOnly } from '../../../services/utils';
import {
  EXPLORE_PROVIDERS,
  MAX_ZIP_CAN_LENGTH,
  MAX_ZIP_LENGTH,
} from '../../../constants';
import { ModalContainer } from '../../common';
import LoadingActionButtonMyPets
  from '../../common/petCloud/LoadingActionButtonMyPets';
import { FoundVetItem } from './FoundVetItem';
import { MyPetsSearchInput } from './MyPetsSearchInput';
import { MyPetsVetManualAdd } from './MyPetsAddVetManuallyModal';
import { ActionButton } from '../../common/petCloud/ActionButton';
import { AddAVetModal } from './AddAVetModal';
import { AddAVetConfirm } from './AddAVetConfirm';

import { useManualVet, useUserBrands } from '../../../hooks';

import './MyPetsAddVetModal.css';

const MyPetsAddVetModal = ({
  onClose = () => { },
  selectedVet = null,
  onSelectedVet = () => { },
  primaryVet = false,
}) => {
  const store = useSelector(({ personalInformation, myVets }) => ({
    ...myVets,
    userZipCode: personalInformation.zipCode,
  }), shallowEqual);
  const dispatch = useDispatch();
  const { t } = useTranslation('myPets');
  const [selectZipcodeMode, setSelectZipcodeMode] = useState(false);
  const [zipCodeError, setZipCodeError] = useState('');
  const [isManuallyModalOpen, setIsManuallyModalOpen] = useState(false);
  const isManualVetRedesign = useManualVet();
  const { hasCanadianPolicies } = useUserBrands();

  const zipcodeMaxLength = hasCanadianPolicies
    ? MAX_ZIP_CAN_LENGTH : MAX_ZIP_LENGTH;

  const [
    zipCode,
    setZipCode,
  ] = useState(store.userZipCode?.slice(0, zipcodeMaxLength));
  const previousZipCodeRef = useRef(zipCode);

  const {
    addVetError,
    favoriteVets,
    isEditingZipCode,
    isLoadingVets,
    isSavingVet,
    isValidatingZipCode,
    isValidZipCode,
    userZipCode,
    vetSaved,
    vetsFound,
  } = store;

  useEffect(() => {
    if (!isValidatingZipCode) {
      if (zipCodeError === '' && !isValidZipCode && !isEditingZipCode) {
        setZipCodeError(t('MyVets.addVetModal.errorZipCode'));
      }

      if (isValidZipCode && !isEditingZipCode) {
        setSelectZipcodeMode(false);
      }
    }
  }, [t, isValidZipCode, isEditingZipCode, zipCodeError, isValidatingZipCode]);

  const favoriteVetIds = useMemo(() => new Set(
    favoriteVets.map((vet) => vet.PlaceId || vet.id),
  ), [favoriteVets]);

  function switchToChangeZipCode() {
    setSelectZipcodeMode(true);
  }

  function handleClose() {
    onClose();
    dispatch(cleanMyVetsErrors());
    onSelectedVet(null);
    setSelectZipcodeMode(false);
    setZipCode(userZipCode);
    setZipCodeError('');
  }

  function handleCancel() {
    if (selectZipcodeMode) {
      setZipCode(previousZipCodeRef.current);
      setSelectZipcodeMode(false);
      setZipCodeError('');
      return;
    }

    if (!isSavingVet || !isValidatingZipCode) {
      handleClose();
    }
  }

  function handleSaveChangeZipCode() {
    if (zipCode.length < MAX_ZIP_LENGTH) {
      setZipCodeError(t('MyVets.addVetModal.errorDigits'));
      return;
    }

    dispatch(validateZipCode({ zipCode }));
    setZipCodeError('');
  }

  function handleSave() {
    if (!selectZipcodeMode && selectedVet) {
      if (primaryVet) {
        dispatch(savePrimaryVet({
          selectedVetId: selectedVet.PlaceId || selectedVet.Id,
        }));
      } else {
        dispatch(addToFavorites({
          categoryId: 1,
          placeId: selectedVet.PlaceId || selectedVet.Id,
          providerId: EXPLORE_PROVIDERS.google,
        }));
      }
      return;
    }

    handleSaveChangeZipCode();
  }

  function handleOnKeyDown(event) {
    const { keyCode } = event;
    const ENTER_KEY = 13;

    if (keyCode === ENTER_KEY) {
      handleSaveChangeZipCode();
    }
  }

  useEffect(() => {
    if (vetSaved && selectedVet) {
      handleClose();
    }
  }, [vetSaved]);

  function renderFoundItem(item) {
    return (
      <FoundVetItem
        key={item.PlaceId || item.Id}
        added={favoriteVetIds.has(item.PlaceId || item.Id)}
        addedLabel={t('MyVets.addVetModal.added')}
        address={item.Address}
        name={item.Title || item.Name}
        showDistance={false}
      />
    );
  }

  function handleOpenAddVetManuallyModal() {
    setIsManuallyModalOpen(true);
  }

  function handleCloseAddVetManuallyModal() {
    setIsManuallyModalOpen(false);
  }

  function handleSuccesCustomVetModal() {
    setIsManuallyModalOpen(false);
    onClose();
  }

  const renderNotListedMessage = () => (
    <>
      <hr className="My-pets-add-vet-modal-not-listed-hr" />

      <button
        className={`${isManualVetRedesign
          ? ''
          : 'Like-span'} My-pets-add-vet-modal-not-listed`}
        onClick={handleOpenAddVetManuallyModal}
        type="button"
      >
        {t('MyVets.addVetModal.notListed')}

        &nbsp;

        {t('MyVets.addVetModal.enterManually')}
      </button>
    </>
  );

  function handleChangeZipCode(event) {
    const { value } = event.target;
    const cleanText = value ? value.replace(/[()-\s]/g, '') : '';
    const onlyNumbersValue = validateNumbersOnly(cleanText)
      ? cleanText
      : cleanText.replace(/\D/g, '');
    setZipCode(hasCanadianPolicies ? value : onlyNumbersValue);

    const validation = hasCanadianPolicies
      ? onlyNumbersValue.length >= MAX_ZIP_LENGTH
      : onlyNumbersValue.length === MAX_ZIP_LENGTH;

    if (validation) {
      setZipCodeError('');
      previousZipCodeRef.current = onlyNumbersValue;
    }

    dispatch(vetZipCodeChanged());
  }

  function handleEmptyItemsFiltered(query) {
    const action = primaryVet ? searchPrimaryVet : searchVets;

    dispatch(action({ query, zipCode }));
  }

  const labels = useMemo(() => {
    const response = {
      leftButtonText: t('MyVets.addVetModal.next'),
      subtitle: t('MyVets.subtitle'),
    };

    if (selectZipcodeMode) {
      response.subtitle = t('MyVets.addVetModal.zipCodeSubtitle');
      response.leftButtonText = t('MyVets.addVetModal.save');
    }

    if (selectedVet) {
      response.subtitle = t('MyVets.checkInfo');
      response.leftButtonText = t('MyVets.addVetModal.add');
    }

    return response;
  }, [selectZipcodeMode, selectedVet, t]);

  const VetModalActionButtons = () => (
    <div className="My-pets-add-vet-modal-actions">
      <LoadingActionButtonMyPets
        disabled={!(selectZipcodeMode || selectedVet)}
        loading={isSavingVet || isValidatingZipCode}
        onClick={handleSave}
        text={labels.leftButtonText}
        type="button"
      />

      <ActionButton
        isSecondaryButton
        onClick={handleCancel}
        title={t('MyVets.addVetModal.cancel')}
      />
    </div>
  );

  function renderSelectedItem() {
    if (isManualVetRedesign) {
      const vetInfoState = {
        vetAddressValue1: selectedVet.AddressLine1 || selectedVet.Address,
        vetAddressValue2: selectedVet.AddressLine2 || '',
        vetCompanyName: selectedVet.Title || selectedVet.Name,
      };

      return (
        <div className="My-pets-add-vet-modal-confirm-vet">
          <AddAVetConfirm state={vetInfoState} />

          <VetModalActionButtons />
        </div>
      );
    }

    return (
      <>
        <div className="My-pets-add-vet-modal-selected-vet">
          <span className="My-pets-add-vet-modal-vet-name">
            {selectedVet.Title || selectedVet.Name}
          </span>

          <div className="My-pets-add-vet-modal-vet-address">
            <span className="My-pets-add-vet-modal-vet-address-location">
              {selectedVet.AddressLine1 || selectedVet.Address}
            </span>

            <span className="My-pets-add-vet-modal-vet-address-location">
              {selectedVet.AddressLine2}
            </span>
          </div>
        </div>

        <VetModalActionButtons />
      </>
    );
  }

  const renderContent = () => {
    if (selectZipcodeMode) {
      return (
        <>
          <span className="My-pets-add-vet-modal-search">
            {t('MyVets.addVetModal.zipCode')}
          </span>

          <div className="My-pets-add-vet-zip-code-input-container">
            <input
              className={`My-pets-add-vet-zip-code-input${zipCodeError
                ? '-error' : ''}`}
              maxLength={zipcodeMaxLength}
              onChange={handleChangeZipCode}
              onKeyDown={handleOnKeyDown}
              placeholder={t('MyVets.addVetModal.searchNearLocation')}
              value={zipCode}
            />

            <VetModalActionButtons />
          </div>

          {zipCodeError ? (
            <span className="My-pets-add-vet-zip-code-error">
              {zipCodeError}
            </span>
          )
            : null}
        </>
      );
    }

    return (
      <>
        {!selectedVet && (
          <div>
            <span className="My-pets-add-vet-modal-search">
              {t('MyVets.addVetModal.searchingNear')}
            </span>

            <span className="My-pets-add-vet-modal-zipcode">
              {zipCode}
            </span>

            <span className="My-pets-separator">
              |
            </span>

            <button
              className="My-pets-add-vet-modal-change Like-span"
              disabled={isLoadingVets}
              onClick={switchToChangeZipCode}
              type="button"
            >
              {t('MyVets.addVetModal.change')}
            </button>
          </div>
        )}

        <MyPetsSearchInput
          emptyMessage={renderNotListedMessage()}
          hideInput={isManuallyModalOpen || selectZipcodeMode}
          id="addVet"
          isLoadingVets={isLoadingVets}
          itemListRender={renderFoundItem}
          itemSelected={selectedVet}
          itemsFound={vetsFound}
          onSearch={handleEmptyItemsFiltered}
          onSelected={onSelectedVet}
          placeholder={t('MyVets.addVetModal.searchByName')}
          renderSelectedItem={renderSelectedItem}
        />
      </>
    );
  };

  return (
    <>
      <ModalContainer show>
        <div className="My-pets-add-vet-modal">
          <button
            className="My-pets-add-vet-modal-close"
            onClick={handleCancel}
            type="button"
          >
            <img alt="" src={close} />
          </button>

          <h2>
            {t('MyVets.addAVet')}
          </h2>

          <p className="My-pets-add-vet-modal-subtitle">
            {labels.subtitle}
          </p>

          {renderContent()}

          {addVetError ? (
            <span className="My-pets-add-vet-zip-code-error">
              {t('MyVets.addVetModal.errorAdding')}
            </span>
          ) : null}
        </div>
      </ModalContainer>

      {/* a modal inside a modal :skull: */}
      {isManualVetRedesign ? (
        <AddAVetModal
          closeModal={handleCloseAddVetManuallyModal}
          handleSuccesCustomVetModal={handleSuccesCustomVetModal}
          show={isManuallyModalOpen}
        />
      ) : (
        <MyPetsVetManualAdd
          closeModal={handleCloseAddVetManuallyModal}
          handleSuccesCustomVetModal={handleSuccesCustomVetModal}
          onSavePrimaryVet={onSelectedVet}
          primaryVet={primaryVet}
          show={isManuallyModalOpen}
        />
      )}
    </>
  );
};

export { MyPetsAddVetModal };
